<template>
  <div id="web-player">
    <section
      class="web-player"
      :class="{ 'is-active': howl }"
      aria-label="Audio player"
    >
      <div class="web-player__song">
        <div class="web-player__titles">
          <p class="web-player__title">
            <router-link
              v-if="canOpenSong"
              :to="{ name: 'songs.show', params: { id: songId } }"
              >{{ songTitle }}</router-link
            >
            <template v-else>{{ songTitle }}</template>
          </p>
          <p class="web-player__writers">
            <span v-if="audioFileTypeName" class="web-player__version">{{
              audioFileTypeName
            }}</span
            >{{ songwriterNames }}
          </p>
          <p class="web-player__file-name" :title="currentAudioFile.file_name">
            {{ currentAudioFile.file_name }}
          </p>
        </div>
      </div>

      <div class="web-player__center">
        <div class="web-player__controls">
          <button
            type="button"
            class="web-player__control web-player__control--jump"
            aria-label="Back 10 seconds"
            @click="rewind(10)"
          >
            <IconReplay10 />
          </button>
          <button
            type="button"
            class="web-player__play"
            :aria-label="isPlaying ? 'Pause' : 'Play'"
            @click="playPause()"
          >
            <IconPause v-if="isPlaying" />
            <IconPlay v-else class="web-player__play-icon" />
          </button>
          <button
            type="button"
            class="web-player__control web-player__control--jump"
            aria-label="Forward 10 seconds"
            @click="fastForward(10)"
          >
            <IconForward10 />
          </button>
          <button
            type="button"
            class="web-player__control web-player__control--next"
            :class="{ 'is-advancing': isAdvancing }"
            :aria-label="isAdvancing ? 'Loading next song' : 'Next song'"
            :aria-busy="isAdvancing ? 'true' : 'false'"
            :disabled="!nextAudioFile || isAdvancing"
            @click="next()"
          >
            <span
              v-if="isAdvancing"
              class="web-player__spinner"
              aria-hidden="true"
            ></span>
            <IconSkipForward v-else />
          </button>
        </div>
        <div class="web-player__progress">
          <span class="web-player__time">{{ elapsedTime }}</span>
          <input
            type="range"
            class="web-player__slider"
            min="0"
            max="100"
            step="0.1"
            aria-label="Seek"
            :aria-valuetext="elapsedTime + ' of ' + totalTime"
            :value="progress"
            :style="{ '--fill': progress + '%' }"
            @input="scrub($event)"
            @change="seekTo($event)"
          />
          <span class="web-player__time">-{{ remainingTime }}</span>
        </div>
      </div>

      <div class="web-player__actions">
        <router-link
          v-if="$store.getters.getActiveQueue === 'user'"
          :to="{ name: 'audio-player-queue' }"
          class="web-player__queue"
        >
          <IconList />
          <span>Up Next</span>
        </router-link>
        <div class="web-player__volume">
          <button
            type="button"
            class="web-player__control"
            :aria-label="isSilent ? 'Unmute' : 'Mute'"
            @click="toggleMute()"
          >
            <IconVolumeX v-if="isSilent" />
            <IconVolume2 v-else />
          </button>
          <input
            v-if="canSetVolume"
            type="range"
            class="web-player__slider"
            min="0"
            max="1"
            step="0.01"
            aria-label="Volume"
            :value="volumePercentage"
            :style="{ '--fill': volumePercentage * 100 + '%' }"
            @input="changeVolume($event)"
          />
        </div>
        <button
          type="button"
          class="web-player__control"
          aria-label="Close player"
          @click="close"
        >
          <IconX />
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import {
  IconForward10,
  IconList,
  IconPause,
  IconPlay,
  IconReplay10,
  IconSkipForward,
  IconVolume2,
  IconVolumeX,
  IconX
} from '@/Icons/IconBase'
import AudioFileEventsApi from '@/services/api/AudioFileEvents'
import {
  AUDIO_PLAYER_LOAD,
  NOTIFY_PITCHED_SONG_LISTENED_TO,
  QUEUED_AUDIO_FILE_SESSION_ALL,
  QUEUED_AUDIO_FILE_SESSION_UPDATE,
  QUEUED_AUDIO_FILE_USER_ALL,
  QUEUED_AUDIO_FILE_USER_UPDATE
} from '@/store/actions'
import { AUDIO_PLAYER_CLEAR_STORE } from '@/store/mutations'
import { Howl, Howler } from 'howler'
import moment from 'moment'

// Audio URLs are signed and expire (S3_URL_TTL_MINUTES on the API, 120 by default). The
// browser keeps requesting byte ranges of the same URL while it streams, so a track
// resumed or seeked after the URL expires would stall. Refresh well before that.
const SIGNED_URL_REFRESH_AFTER_MS = 100 * 60 * 1000

export default {
  name: 'AudioPlayer',

  components: {
    IconPlay,
    IconReplay10,
    IconForward10,
    IconSkipForward,
    IconVolumeX,
    IconVolume2,
    IconX,
    IconList,
    IconPause
  },

  props: ['contactId', 'pitchId'],

  data() {
    return {
      howl: null,
      duration: 0,
      progress: 0,
      elapsed: 0,
      isMuted: false,
      volume: 1,
      playbackSessionId: null,
      playingEventInterval: 5,
      lastPlayingEventTime: null,
      latestPlayStartTime: null,
      isPlaying: false,
      nextAudioFile: null,
      progressFrame: null,
      isScrubbing: false,
      canSetVolume: true,
      isAdvancing: false,
      advanceTimeout: null,
      urlFetchedAt: null,
      pendingResume: null,
      hasRetriedLoad: false
    }
  },

  computed: {
    currentAudioFile: function () {
      return this.$store.state.audioPlayer.audioFile
    },

    audioPlayerStatus: function () {
      return this.$store.state.audioPlayer.status
    },

    songId: function () {
      return this.currentAudioFile.song ? this.currentAudioFile.song.id : null
    },

    // Pitch link listeners aren't signed in, and the song page would send them to the login screen.
    canOpenSong: function () {
      return !!this.songId && this.$store.getters.isAuthenticated
    },

    songTitle: function () {
      return this.currentAudioFile.song ? this.currentAudioFile.song.title : ''
    },

    songwriterNames: function () {
      return this.currentAudioFile.song
        ? this.currentAudioFile.song.songwriter_names
        : ''
    },

    audioFileTypeName: function () {
      return this.currentAudioFile.type ? this.currentAudioFile.type.name : null
    },

    totalTime: function () {
      return this.formatTime(this.duration)
    },

    elapsedTime: function () {
      return this.formatTime(this.elapsed)
    },

    remainingTime: function () {
      return this.formatTime(Math.max(0, this.duration - this.elapsed))
    },

    volumePercentage: function () {
      if (this.isMuted) {
        return 0
      }
      return this.volume
    },

    isSilent: function () {
      return this.volumePercentage === 0
    },

    queuedAudioFiles: function () {
      return this.$store.getters.getQueuedAudioFiles
    }
  },

  watch: {
    // Lets page scroll areas leave room at the bottom so the player doesn't cover content.
    // Loading the next song's details failed, so it will never start; release the Next button.
    audioPlayerStatus: function (newValue) {
      if (newValue === 'error') {
        this.stopAdvancing()
        this.pendingResume = null
      }
    },

    howl: function (newValue) {
      document.body.classList.toggle('has-web-player', !!newValue)
    },

    queuedAudioFiles: function (newValue) {
      if (newValue.length) {
        this.nextAudioFile = newValue[0]
      } else {
        this.nextAudioFile = null
      }
    },
    currentAudioFile: function (newValue) {
      this.unload()

      if (!newValue.url) {
        return
      }

      this.playbackSessionId = this.$store.getters.getSessionId
      this.lastPlayingEventTime = null
      this.urlFetchedAt = Date.now()

      // Set when this load only refreshes the current track's expired URL.
      const resume = this.pendingResume
      this.pendingResume = null
      if (!resume) {
        this.hasRetriedLoad = false
      }

      let self = this

      // Start as soon as enough is buffered to begin ('canplay') rather than Howler's default of
      // waiting until the browser expects to play the whole file without pausing ('canplaythrough'),
      // which for large WAVs can mean downloading most of the file first. This is a Howler internal,
      // so recheck it when upgrading Howler.
      Howler._canPlayEvent = 'canplay'

      this.howl = new Howl({
        src: this.currentAudioFile.url,
        // Stream rather than download and decode the whole file before playing, which is slow on
        // phones and with large WAVs.
        html5: true,
        volume: this.volume,
        onload: (id) => {
          console.log('onload fired')
          self.setDuration()

          if (self.volume === null) {
            self.setVolume(0.25)
          }

          if (resume) {
            self.howl.seek(resume.position)
            self.latestPlayStartTime = resume.position
            self.elapsed = resume.position
            if (!resume.playing) {
              self.updateProgress()
              return
            }
          }

          self.play()
          self.updateProgress()
        },
        onloaderror: (id, error) => {
          console.log('onloaderror fired', error)

          // Most often the signed URL expired mid-stream; get a fresh one once and carry on.
          if (!self.hasRetriedLoad) {
            self.hasRetriedLoad = true
            self.refreshUrl(self.elapsed, self.isPlaying || !self.duration)
            return
          }

          self.stopAdvancing()
          self.$store.commit('error', 'Could not load audio file.')
        },
        onplay: (id) => {
          console.log('onplay fired')
          self.stopAdvancing()
        },
        onplayerror: (id, error) => {
          console.log('onplayerror fired', error)
          // Mobile browsers block playback that doesn't start directly from a tap. Howler unlocks
          // audio on the next tap anywhere on the page, so start playing then.
          self.isPlaying = false
          self.stopAdvancing()
          self.howl.once('unlock', () => self.play())
        },
        onpause: (id) => {
          console.log('onpause fired')
        },
        onstop: (id) => {
          self.lastPlayingEventTime = null
          console.log('onstop fired')
        },
        onend: (id) => {
          console.log('onend fired')
          self.next()
          self.isPlaying = false
          self.recordEvent('end', self.elapsed)
        },
        onmute: (id) => {
          console.log('onmute fired')
        },
        onvolume: (id) => {
          console.log('onvolume fired')
        },
        onrate: (id) => {
          console.log('onrate fired')
        },
        onseek: (id) => {
          console.log('onseek fired')
          // When streaming, Howler pauses internally to seek and resumes without a play event,
          // which stops the progress loop. Restart it once the seek completes.
          self.updateProgress()
        },
        onfade: (id) => {
          console.log('onfade fired')
        },
        onunlock: (id) => {
          console.log('onunlock fired')
        }
      })
    }
  },

  mounted() {
    this.canSetVolume = this.detectVolumeSupport()
  },

  beforeDestroy() {
    document.body.classList.remove('has-web-player')
    clearTimeout(this.advanceTimeout)
  },

  methods: {
    close() {
      this.stopAdvancing()

      if (this.howl) {
        this.howl.fade(this.volume, 0, 250)
        this.$store.commit(AUDIO_PLAYER_CLEAR_STORE)
      }

      let self = this
      setTimeout(function () {
        self.unload()
        setTimeout(function () {
          self.duration = 0
          self.elapsed = 0
          self.progress = 0
        }, 500)
      }, 250)
    },

    unload() {
      if (this.howl) {
        this.howl.unload()
        this.howl = null
      }
    },

    updateProgress() {
      // Only one progress loop may run, or playing events get recorded more than once.
      cancelAnimationFrame(this.progressFrame)

      if (!this.howl) {
        return
      }

      if (this.howl.state() === 'loaded' && !this.isScrubbing) {
        this.elapsed = this.howl.seek()
        this.progress = this.duration ? (this.elapsed / this.duration) * 100 : 0

        if (this.howl.playing()) {
          this.executePlayingEvent(this.elapsed)
        }
      }

      // After a seek into a part that hasn't streamed yet, Howler reports the sound as loading and
      // resumes playback without an event. Keep looping while the user has it playing so progress
      // picks up again as soon as Howler is ready.
      if (this.isPlaying) {
        this.progressFrame = requestAnimationFrame(this.updateProgress)
      }
    },

    setDuration() {
      this.duration = this.howl.duration()
    },

    setVolume(value) {
      this.volume = value
      this.howl.volume(this.volume)
    },

    play() {
      this.recordEvent('play', this.elapsed)
      this.howl.play()
      this.latestPlayStartTime = this.howl.seek()
      this.isPlaying = true
      this.updateProgress()
    },

    seek(position) {
      if (this.isUrlStale()) {
        return this.refreshUrl(position, this.isPlaying)
      }

      this.howl.seek(position)
      this.latestPlayStartTime = this.howl.seek()
    },

    playPause() {
      if (this.howl.playing()) {
        this.recordEvent('pause', this.elapsed)
        this.isPlaying = false
        return this.howl.pause()
      }

      if (this.isUrlStale()) {
        return this.refreshUrl(this.howl.seek(), true)
      }

      return this.play()
    },

    isUrlStale() {
      return !!this.urlFetchedAt && Date.now() - this.urlFetchedAt > SIGNED_URL_REFRESH_AFTER_MS
    },

    // Reloads the current track with a freshly signed URL, then resumes from position.
    refreshUrl(position, playing) {
      this.pendingResume = { position, playing }
      this.$store.dispatch(AUDIO_PLAYER_LOAD, {
        id: this.currentAudioFile.id,
        pitchId: this.currentAudioFile.pitch_id || null
      })
    },

    stop() {
      this.recordEvent('stop', this.elapsed)
      this.howl.stop()
      this.isPlaying = false
    },

    rewind(secs) {
      let currPosition = this.howl.seek()
      let newPosition =
        currPosition - parseInt(secs) > 0 ? currPosition - parseInt(secs) : 0
      this.recordEvent('rewind', newPosition)
      this.seek(newPosition)
    },

    fastForward(secs) {
      let currPosition = this.howl.seek()
      let newPosition = currPosition + parseInt(secs)
      let duration = this.howl.duration()
      if (newPosition < duration) {
        this.recordEvent('fast-forward', newPosition)
        this.seek(newPosition)
      } else {
        this.stop()
      }
    },

    // While dragging, preview the position; seek once on release so streaming isn't asked to seek repeatedly.
    scrub(event) {
      this.isScrubbing = true
      this.progress = Number(event.target.value)
      this.elapsed = (this.progress / 100) * this.duration
    },

    seekTo(event) {
      this.isScrubbing = false
      let newPosition = (Number(event.target.value) / 100) * this.duration

      if (newPosition < this.duration) {
        this.recordEvent('seek', newPosition)
        this.seek(newPosition)
      } else {
        this.stop()
      }
    },

    mute() {
      this.howl.mute(!this.isMuted)
      this.isMuted = !this.isMuted
    },

    // iOS and iPadOS ignore volume changes from web pages (only the hardware buttons work), so a
    // volume set on a test element reads back unchanged there.
    detectVolumeSupport() {
      const audio = document.createElement('audio')
      audio.volume = 0.5
      return audio.volume === 0.5
    },

    toggleMute() {
      if (!this.isSilent) {
        this.mute()
        return
      }

      if (this.isMuted) {
        this.mute()
      }
      if (this.volume === 0) {
        this.setVolume(0.5)
      }
    },

    changeVolume(event) {
      if (this.isMuted) {
        this.mute()
      }
      this.setVolume(Number(event.target.value))
    },

    formatTime(totalSeconds) {
      let minutes = Math.floor(totalSeconds / 60)
      let seconds = Math.floor(totalSeconds - minutes * 60)
      return minutes + ':' + String(seconds).padStart(2, '0')
    },

    executePlayingEvent(currentTime) {
      let currentPlayDuration = currentTime - this.latestPlayStartTime

      // Don't record playing event if playing for less than 3 seconds
      if (currentPlayDuration < 3) {
        return
      }

      if (
        this.lastPlayingEventTime === null ||
        currentTime - this.lastPlayingEventTime > this.playingEventInterval
      ) {
        this.lastPlayingEventTime = currentTime
        this.recordEvent('playing', currentTime)
      }
    },

    recordEvent(type, currentTime) {
      const contactId = this.contactId ? this.contactId : null
      let pitchId = this.currentAudioFile.pitch_id
        ? this.currentAudioFile.pitch_id
        : null
      AudioFileEventsApi.storeEvent(
        this.currentAudioFile.id,
        type,
        currentTime,
        this.playbackSessionId,
        this.duration,
        contactId,
        pitchId
      )
    },

    next() {
      if (this.isAdvancing) {
        return
      }

      const activeQueue = this.$store.getters.getActiveQueue
      if (activeQueue !== 'session' && activeQueue !== 'user') {
        return
      }

      this.startAdvancing()

      if (activeQueue === 'session') {
        this.incrementSessionQueue()
      } else {
        this.incrementUserQueue()
      }
    },

    // Disables Next and shows its spinner until the next song starts playing (or fails to).
    startAdvancing() {
      this.isAdvancing = true
      clearTimeout(this.advanceTimeout)
      // Safety net for failures that never report back, so the button can't stay stuck.
      this.advanceTimeout = setTimeout(() => this.stopAdvancing(), 20000)
    },

    stopAdvancing() {
      this.isAdvancing = false
      clearTimeout(this.advanceTimeout)
    },

    incrementSessionQueue() {
      let self = this
      this.$store
        .dispatch(
          QUEUED_AUDIO_FILE_SESSION_ALL,
          this.$store.getters.getSessionId
        )
        .then(
          function (data) {
            if (data.length) {
              self.recordEvent('next', self.elapsed)
              self.$store.dispatch(AUDIO_PLAYER_LOAD, {
                id: data[0].audio_file_id,
                pitchId: data[0].pitch_id
              })
              if (data[0].pitch_id) {
                self.$store.dispatch(NOTIFY_PITCHED_SONG_LISTENED_TO, {
                  song: data[0].audio_file.song.id,
                  pitch: data[0].pitch_id
                })
              }
              self.$store
                .dispatch(QUEUED_AUDIO_FILE_SESSION_UPDATE, {
                  id: data[0].id,
                  data: {
                    played_at: moment().utc().format('YYYY-MM-DD HH:mm:ss')
                  }
                })
                .then(function () {
                  self.$store.dispatch(
                    QUEUED_AUDIO_FILE_SESSION_ALL,
                    self.$store.getters.getSessionId
                  )
                })
            } else {
              self.stopAdvancing()
            }
          },
          function (err) {
            self.stopAdvancing()
            self.$store.commit('error', 'Could not load audio player queue.')
            console.log(err)
          }
        )
    },

    incrementUserQueue() {
      let self = this
      this.$store.dispatch(QUEUED_AUDIO_FILE_USER_ALL).then(
        function (data) {
          if (data.length) {
            self.recordEvent('next', self.elapsed)
            self.$store.dispatch(AUDIO_PLAYER_LOAD, {
              id: data[0].audio_file_id
            })
            self.$store
              .dispatch(QUEUED_AUDIO_FILE_USER_UPDATE, {
                id: data[0].id,
                data: {
                  played_at: moment().utc().format('YYYY-MM-DD HH:mm:ss')
                }
              })
              .then(function () {
                self.$store.dispatch(QUEUED_AUDIO_FILE_USER_ALL)
              })
          } else {
            self.stopAdvancing()
          }
        },
        function (err) {
          self.stopAdvancing()
          self.$store.commit('error', 'Could not load audio player queue.')
          console.log(err)
        }
      )
    }
  }
}
</script>
